import { Box, Card, Skeleton, Stack, Tabs, Title } from "@mantine/core";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { DataGrid } from "@/components/elements";
import { useMarketCar } from "@/services/market/get-market-car";

import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type CarFactsheetProps = {
  carId: string;
  defaultTab?: string;
  tab?: { name: string; children: ReactNode };
} & MantineStyleProps;

export const CarFactsheet: FC<CarFactsheetProps> = ({ carId, defaultTab, tab, ...props }) => {
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState(defaultTab || tab?.name || null);
  const { data, isLoading, isSuccess } = useMarketCar({ carId });
  const { factsheet = [] } = data || {};
  const hasTabs = factsheet?.length > 0 || !!tab;

  const tabName = tab?.name;
  useEffect(() => {
    if (isSuccess && !currentTab) {
      setCurrentTab(factsheet?.[0].name || tabName || null);
    }
  }, [data, isSuccess, tabName]);

  return (
    hasTabs && (
      <Card>
        <Stack {...props}>
          <Title order={3}>{t("car.sections.factsheet")}</Title>
          <Skeleton visible={isLoading}>
            <Tabs value={currentTab} onChange={setCurrentTab} variant="pills">
              <Tabs.List>
                {factsheet?.map((item) => (
                  <Tabs.Tab key={item.name} value={item.name}>
                    {t(`car.sections.${item.name}`, item.name)}
                  </Tabs.Tab>
                ))}
                {tab && (
                  <Tabs.Tab value={tab.name}>{t(`car.sections.${tab.name}`, tab.name)}</Tabs.Tab>
                )}
              </Tabs.List>
              <Box mt="md">
                {factsheet?.map((item) => (
                  <Tabs.Panel key={item.name} value={item.name}>
                    <DataGrid
                      cols={{ base: 1, md: 2 }}
                      title=""
                      items={item.values.map(({ label, value }) => ({
                        label: t(`car.factsheet.${item.name}.${label}`, label),
                        value,
                      }))}
                    />
                  </Tabs.Panel>
                ))}
                {tab && <Tabs.Panel value={tab.name}>{tab.children}</Tabs.Panel>}
              </Box>
            </Tabs>
          </Skeleton>
        </Stack>
      </Card>
    )
  );
};
